import { useCallback, useEffect, useState } from 'react';

import {
  getStorageEstimate,
  shouldBlockCapture,
  shouldWarnUsage,
} from '../infrastructure/storage-guard.js';

export interface StorageGuardState {
  /** True when free space is below the 15MB capture floor (design §5 quota table). */
  blocked: boolean;
  /** True at/above 80% quota usage — a heads-up before it becomes a hard block. */
  warnUsage: boolean;
}

export interface UseStorageGuardResult extends StorageGuardState {
  /**
   * MEDIUM fix (adversarial review) — "re-check on submit, not only on
   * mount". Re-reads `navigator.storage.estimate()` RIGHT NOW, updates the
   * hook's own state (so the UI reflects it too), and resolves the fresh
   * result directly — a caller gating an action (CheckinPage's submit)
   * needs the CURRENT answer synchronously in that same call, not
   * whatever `blocked` happened to be at the component's last render
   * (which can go stale over a long-lived session as captures consume
   * storage between mount and this submit).
   */
  refresh: () => Promise<StorageGuardState>;
}

const NOT_BLOCKED: StorageGuardState = { blocked: false, warnUsage: false };

async function readStorageGuardState(): Promise<StorageGuardState> {
  const storage = typeof navigator !== 'undefined' ? navigator.storage : undefined;
  const estimate = await getStorageEstimate(storage);
  return {
    blocked: shouldBlockCapture(estimate),
    warnUsage: shouldWarnUsage(estimate),
  };
}

/**
 * useStorageGuard (pwa-offline-first Slice 7, task 7.8 — "wire
 * storage-guard.ts live") — reads `navigator.storage.estimate()` once on
 * mount and resolves to the SAME fail-open semantics `storage-guard.ts`'s
 * own pure predicates already encode: an unsupported browser (older
 * Safari, or a test environment with no `navigator.storage`) is NEVER
 * blocked. Re-checks on every `refreshToken` change (callers pass a value
 * that changes after a capture/sync, e.g. the pending count) so the
 * estimate does not go stale for the lifetime of a long session. Also
 * exposes `refresh()` (MEDIUM fix — see its own doc comment) for a
 * caller-driven re-check outside the mount/refreshToken cadence.
 */
export function useStorageGuard(refreshToken?: unknown): UseStorageGuardResult {
  const [state, setState] = useState<StorageGuardState>(NOT_BLOCKED);

  // biome-ignore lint/correctness/useExhaustiveDependencies: refreshToken is an intentional re-check trigger, not a value read inside the effect.
  useEffect(() => {
    let cancelled = false;
    void readStorageGuardState().then((next) => {
      if (!cancelled) setState(next);
    });
    return () => {
      cancelled = true;
    };
  }, [refreshToken]);

  const refresh = useCallback(async (): Promise<StorageGuardState> => {
    const next = await readStorageGuardState();
    setState(next);
    return next;
  }, []);

  return { ...state, refresh };
}
